import React, { useEffect, useMemo, useState } from 'react';

const KEYFRAME_STYLE_ID = 'ahc-keyframes';

function ensureKeyframes() {
  if (typeof document === 'undefined' || document.getElementById(KEYFRAME_STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = KEYFRAME_STYLE_ID;
  el.textContent =
    '@keyframes ahc-fade-in{from{opacity:0;transform:translateY(5px)}to{opacity:1;transform:translateY(0)}}' +
    '@keyframes ahc-dot{0%,80%,100%{transform:translateY(0);opacity:.3}40%{transform:translateY(-5px);opacity:1}}' +
    '.ahc-content{counter-reset:ahc-ol}' +
    '.ahc-content ul,.ahc-content ol{list-style:none;margin:0;padding:0}' +
    '.ahc-content li{padding-left:1.25em}' +
    '.ahc-content li.ahc-ul{display:list-item;list-style-type:disc;list-style-position:inside}' +
    '.ahc-content li.ahc-ol{display:block;counter-increment:ahc-ol}' +
    '.ahc-content li.ahc-ol::before{content:counter(ahc-ol) ". ";font-weight:600}' +
    '.ahc-content strong,.ahc-content b{font-weight:700}' +
    '.ahc-content em,.ahc-content i{font-style:italic}' +
    '.ahc-content a{color:#1492ff;text-decoration:underline}' +
    '.ahc-content h1{font-size:1.5em;font-weight:700}' +
    '.ahc-content h2{font-size:1.25em;font-weight:700}' +
    '.ahc-content h3{font-size:1.125em;font-weight:600}' +
    '.ahc-content h4,.ahc-content h5,.ahc-content h6{font-weight:600}' +
    '.ahc-content code{font-family:monospace;background:rgba(0,0,0,.08);padding:.1em .3em;border-radius:3px;font-size:.9em}' +
    '.ahc-content blockquote{border-left:3px solid currentColor;padding-left:.75em;opacity:.75}';
  document.head.appendChild(el);
}

ensureKeyframes();

// Tags treated as individual animated units
const BLOCK_TAGS = new Set(['P', 'LI', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE']);
// Tags we recurse into rather than animate as a whole
const CONTAINER_TAGS = new Set(['DIV', 'UL', 'OL', 'SECTION', 'ARTICLE', 'MAIN', 'BODY']);

function collectBlocks(node: Node, blocks: string[], listType?: 'ahc-ul' | 'ahc-ol') {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = (node.textContent ?? '').trim();
    if (text) blocks.push(text);
    return;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return;
  const el = node as HTMLElement;
  if (el.tagName === 'BR') return;
  if (el.tagName === 'LI') {
    if (el.innerHTML.trim()) {
      if (listType) el.classList.add(listType);
      const wrapperTag = listType === 'ahc-ol' ? 'ol' : 'ul';
      blocks.push(`<${wrapperTag}>${el.outerHTML}</${wrapperTag}>`);
    }
  } else if (BLOCK_TAGS.has(el.tagName)) {
    if (el.innerHTML.trim()) blocks.push(el.outerHTML);
  } else if (el.tagName === 'UL') {
    el.childNodes.forEach((child) => collectBlocks(child, blocks, 'ahc-ul'));
  } else if (el.tagName === 'OL') {
    el.childNodes.forEach((child) => collectBlocks(child, blocks, 'ahc-ol'));
  } else if (CONTAINER_TAGS.has(el.tagName)) {
    el.childNodes.forEach((child) => collectBlocks(child, blocks, listType));
  } else if (el.innerHTML.trim()) {
    blocks.push(el.outerHTML);
  }
}

function parseBlocks(html: string): string[] {
  if (!html?.trim()) return [];

  const parser = new DOMParser();
  const { body } = parser.parseFromString(html, 'text/html');
  const blocks: string[] = [];

  body.childNodes.forEach((node) => collectBlocks(node, blocks));

  // Fallback: try splitting by <br> tags
  if (blocks.length <= 1) {
    const brParts = html
      .split(/<br\s*\/?>/i)
      .map((s) => s.trim())
      .filter((s) => s.replace(/<[^>]*>/g, '').trim());
    if (brParts.length > 1) return brParts;
  }

  return blocks.length ? blocks : [html];
}

/**
 * Compute cumulative animation start times with an easing-in curve.
 * The gap between successive lines shrinks exponentially, so the first
 * few lines appear slowly and the pace accelerates toward a minimum.
 */
function computeDelays(count: number): number[] {
  const startGap = 1500; // ms before the 2nd line
  const decay = 0.65; // each subsequent gap is 65% of the previous
  const minGap = 360; // floor once the curve flattens out
  const delays = [0];
  for (let i = 1; i < count; i++) {
    const gap = Math.max(minGap, startGap * Math.pow(decay, i - 1));
    delays.push(delays[i - 1] + gap);
  }
  return delays;
}

type Props = {
  html: string;
  className?: string;
  showLoadingDots?: boolean;
};

const AnimatedHtmlContent: React.FC<Props> = ({ html, className, showLoadingDots = true }) => {
  const blocks = useMemo(() => parseBlocks(html), [html]);
  const delays = useMemo(() => computeDelays(blocks.length), [blocks.length]);

  // visibleCount tracks how many blocks have been added to the DOM.
  // Blocks are added one at a time via timers so the dots always sit
  // directly under the last revealed block.
  const [visibleCount, setVisibleCount] = useState(1);
  const [showDots, setShowDots] = useState(true);

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];

    for (let i = 1; i < delays.length; i++) {
      const idx = i;
      timers.push(setTimeout(() => setVisibleCount(idx + 1), delays[idx]));
    }

    const lastDelay = delays[delays.length - 1] ?? 0;
    timers.push(setTimeout(() => setShowDots(false), lastDelay + 400));

    return () => timers.forEach(clearTimeout);
  }, [delays]);

  return (
    <div className={`ahc-content${className ? ` ${className}` : ''}`}>
      {blocks.slice(0, visibleCount).map((block, i) => (
        <div
          key={i}
          style={{
            opacity: 0,
            animation: 'ahc-fade-in 0.35s ease forwards',
            marginBottom: 12,
          }}
          dangerouslySetInnerHTML={{ __html: block }}
        />
      ))}
      {showDots && showLoadingDots && (
        <div style={{ display: 'flex', gap: 5, padding: '4px 0' }} aria-hidden="true">
          {[0, 1, 2].map((j) => (
            <span
              key={j}
              style={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                backgroundColor: 'currentColor',
                display: 'inline-block',
                animation: 'ahc-dot 1.2s ease-in-out infinite',
                animationDelay: `${j * 0.2}s`,
              }}
            />
          ))}
        </div>
      )}
      {showDots && showLoadingDots && (
        <span role="status" className="sr-only">
          Loading response
        </span>
      )}
    </div>
  );
};

export default AnimatedHtmlContent;
